/**
 * Pure re-anchor logic for document comments (BLA-70 / B1).
 *
 * A comment's durable anchor stores the verbatim `quoted_text` plus best-effort
 * `char_start`/`char_end` offsets. Derivative bodies are editable, so those
 * offsets drift; rather than highlight whatever now sits at them (which could be
 * the wrong text) this resolves the anchor against the CURRENT body text with
 * the same three-branch strategy `blueprint/citationResolve.ts` uses for
 * citations, lifted from line numbers to character offsets:
 *
 *   1. **fresh** — the quoted text still sits exactly at the stored offsets.
 *   2. **moved** — it moved, but is found elsewhere (re-anchored, flagged).
 *   3. **unlocatable** — it is gone; fail loud (no highlight; the sidebar shows
 *      a "text changed" note), never a wrong highlight.
 *
 * A comment with no span anchor (a section-/document-level note) resolves to
 * **unanchored**. This module is headless (no DOM, no ProseMirror) so it unit-
 * tests cleanly and is shared by the editor's decoration glue; the glue maps the
 * returned character range onto ProseMirror positions. Nothing is logged (PHI).
 */
import type { CommentAnchor } from "../../types";

/** The outcome class of resolving a comment anchor against the current body. */
export type CommentAnchorStatus =
  | "fresh"
  | "moved"
  | "unlocatable"
  | "unanchored";

/** The resolved character range (or a sentinel) plus its status. */
export interface ResolvedCommentAnchor {
  /** Where the anchor resolved. */
  status: CommentAnchorStatus;
  /** Start char offset in the body, or -1 when there is nothing to highlight. */
  from: number;
  /** End char offset in the body, or -1 when there is nothing to highlight. */
  to: number;
}

/** Does `quoted` sit exactly at `[start, end]` in `body`? */
function isFreshAt(
  body: string,
  quoted: string,
  start: number,
  end: number,
): boolean {
  return (
    start >= 0 &&
    end <= body.length &&
    start <= end &&
    body.slice(start, end) === quoted
  );
}

/**
 * Find `quoted` in `body`, preferring an occurrence immediately after `prefix`.
 *
 * The prefix disambiguates a short quote that repeats in the body (the
 * TextQuoteSelector idea). When no prefix match exists it falls back to the
 * first plain occurrence.
 *
 * @param body - The current body text.
 * @param quoted - The verbatim text to locate.
 * @param prefix - Optional preceding context to disambiguate repeats.
 * @returns The found `{ from, to }` char range, or `null` when absent.
 */
export function findQuotedRange(
  body: string,
  quoted: string,
  prefix?: string | null,
): { from: number; to: number } | null {
  if (quoted === "") return null;
  if (prefix) {
    const withPrefix = body.indexOf(prefix + quoted);
    if (withPrefix !== -1) {
      const from = withPrefix + prefix.length;
      return { from, to: from + quoted.length };
    }
  }
  const at = body.indexOf(quoted);
  if (at === -1) return null;
  return { from: at, to: at + quoted.length };
}

/**
 * Strip common markdown formatting to produce a plain-text projection
 * comparable to TipTap's extracted text content.
 */
export function stripMarkdown(markdown: string): string {
  return (
    markdown
      // Remove block-level syntax (headers)
      .replace(/^#{1,6}\s+/gm, "")
      // Remove list bullets (e.g., "* ", "- ", "1. ")
      .replace(/^[ \t]*(?:[*+-]|\d+\.)\s+/gm, "")
      // Remove inline citation and inference markers: [[src:...]] and [[inf:...]]
      .replace(/\[\[(?:src|inf):.*?\]\]/g, "")
      // Remove links [text](url)
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      // Remove bold/italic markers
      .replace(/[*_]{1,2}(.*?)[*_]{1,2}/g, "$1")
      // Remove inline code
      .replace(/`([^`]+)`/g, "$1")
      // Normalize block-separating newlines to a single space
      .replace(/\n+/g, " ")
  );
}

/**
 * Resolve a comment's anchor against the current body text.
 *
 * @param anchor - The stored anchor (may be `null`/absent for a document note).
 * @param rawBody - The current markdown body text to resolve against.
 * @returns The resolved status + char range.
 */
export function resolveCommentAnchor(
  anchor: CommentAnchor | null | undefined,
  rawBody: string,
): ResolvedCommentAnchor {
  const body = stripMarkdown(rawBody);
  const quoted = anchor?.quoted_text ?? "";
  if (!anchor || quoted === "") {
    return { status: "unanchored", from: -1, to: -1 };
  }
  const start = anchor.char_start ?? -1;
  const end = anchor.char_end ?? -1;
  if (isFreshAt(body, quoted, start, end)) {
    return { status: "fresh", from: start, to: end };
  }
  const found = findQuotedRange(body, quoted, anchor.prefix);
  if (found) {
    return { status: "moved", from: found.from, to: found.to };
  }
  return { status: "unlocatable", from: -1, to: -1 };
}
